import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';

const SystemOverview = () => {
  const [systemStats, setSystemStats] = useState({});
  const [recentActivities, setRecentActivities] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadSystemData = async () => {
      setLoading(true);
      try {
        // This is where you would fetch real system data from your API
        // For now, showing empty state
        setSystemStats({});
        setRecentActivities([]);
      } catch (error) {
        console.error('Error loading system data:', error);
        setSystemStats({});
        setRecentActivities([]);
      } finally {
        setLoading(false);
      }
    };

    loadSystemData();
  }, []);

  const getActivityIcon = (type) => {
    switch (type) {
      case 'update': return 'Edit';
      case 'approval': return 'CheckCircle';
      case 'upload': return 'Upload';
      case 'system': return 'Settings';
      default: return 'Activity';
    }
  };

  const getActivityColor = (type) => {
    switch (type) {
      case 'update': return 'primary';
      case 'approval': return 'success';
      case 'upload': return 'warning';
      case 'system': return 'accent';
      default: return 'muted';
    }
  };

  const renderLoadingState = () => (
    <div className="space-y-6">
      {/* System Health Loading */}
      <div className="bg-card rounded-lg border border-border card-shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="w-32 h-5 bg-muted rounded animate-pulse"></div>
          <div className="w-5 h-5 bg-muted rounded animate-pulse"></div>
        </div>
        <div className="space-y-4">
          {[1, 2, 3, 4]?.map((i) => (
            <div key={i} className="flex items-center justify-between animate-pulse">
              <div className="w-24 h-4 bg-muted rounded"></div>
              <div className="w-16 h-4 bg-muted rounded"></div>
            </div>
          ))}
        </div>
      </div>

      {/* Recent Activity Loading */}
      <div className="bg-card rounded-lg border border-border card-shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="w-32 h-5 bg-muted rounded animate-pulse"></div>
          <div className="w-5 h-5 bg-muted rounded animate-pulse"></div>
        </div>
        <div className="space-y-3">
          {[1, 2, 3, 4]?.map((i) => (
            <div key={i} className="flex items-center space-x-3 p-2 animate-pulse">
              <div className="w-6 h-6 rounded-full bg-muted"></div>
              <div className="flex-1 min-w-0">
                <div className="w-full h-4 bg-muted rounded mb-1"></div>
                <div className="w-24 h-3 bg-muted rounded"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderEmptySystemHealth = () => (
    <div className="text-center py-8">
      <Icon name="Activity" size={48} color="var(--color-muted)" className="mx-auto mb-4" />
      <p className="text-muted-foreground mb-2">System monitoring unavailable</p>
      <p className="text-sm text-muted-foreground">
        System health metrics will appear here when available
      </p>
    </div>
  );

  const renderEmptyActivity = () => (
    <div className="text-center py-8">
      <Icon name="Clock" size={48} color="var(--color-muted)" className="mx-auto mb-4" />
      <p className="text-muted-foreground mb-2">No recent activity</p>
      <p className="text-sm text-muted-foreground">
        Recent system activities will be shown here
      </p>
    </div>
  );

  if (loading) {
    return renderLoadingState();
  }

  const hasSystemStats = systemStats && Object.keys(systemStats)?.length > 0;

  return (
    <div className="space-y-6">
      {/* System Health */}
      <div className="bg-card rounded-lg border border-border card-shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground">System Health</h3>
          <Icon name="Activity" size={18} color="var(--color-success)" />
        </div>
        
        {!hasSystemStats ? (
          renderEmptySystemHealth()
        ) : (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Uptime</span>
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-success rounded-full"></div>
                <span className="text-sm font-medium text-foreground">{systemStats?.uptime}</span>
              </div>
            </div>
            
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Active Users</span>
              <span className="text-sm font-medium text-foreground">{systemStats?.activeUsers}</span>
            </div>
            
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Data Storage</span>
              <span className="text-sm font-medium text-foreground">{systemStats?.dataStorage}</span>
            </div>
            
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">Last Backup</span>
              <span className="text-sm font-medium text-foreground">{systemStats?.lastBackup}</span>
            </div>
          </div>
        )}
      </div>

      {/* Recent Activity */}
      <div className="bg-card rounded-lg border border-border card-shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-foreground">Recent Activity</h3>
          <Icon name="Clock" size={18} color="var(--color-primary)" />
        </div>
        
        {recentActivities?.length === 0 ? (
          renderEmptyActivity()
        ) : (
          <>
            <div className="space-y-3">
              {recentActivities?.map((activity) => (
                <div
                  key={activity?.id}
                  className="flex items-center space-x-3 p-2 rounded-lg hover:bg-background/50 transition-colors"
                >
                  <div className={`
                    w-6 h-6 rounded-full bg-${getActivityColor(activity?.type)}/10 
                    flex items-center justify-center
                  `}>
                    <Icon 
                      name={getActivityIcon(activity?.type)} 
                      size={12} 
                      color={`var(--color-${getActivityColor(activity?.type)})`} 
                    />
                  </div>
                  
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-foreground">
                      <span className="font-medium">{activity?.user}</span>
                      {' '}{activity?.action}
                    </p>
                    <p className="text-xs text-muted-foreground">{activity?.time}</p>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="mt-4 pt-4 border-t border-border">
              <button className="w-full text-center text-sm text-primary hover:text-primary/80 transition-colors">
                View All Activity
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default SystemOverview;